<template>
    <div class="nested-nav-switcher">
        <primevue-button
            text
            type="button"
            class="nested-nav-trigger"
            :aria-label="triggerAriaLabel"
            :title="triggerAriaLabel"
            @click="openDrawer"
        >
            <span class="nested-nav-label">{{ activeLabel }}</span>
            <font-awesome-icon :icon="['fas', 'bars']" class="nested-nav-chevron" />
        </primevue-button>
        <drawer
            v-model:visible="isDrawerVisible"
            position="left"
            class="nested-nav-drawer drawer-width-50"
            :header="drawerHeader"
        >
            <nav class="nested-nav-list" :aria-label="navAriaLabel">
                <template
                    v-for="item in props.items"
                    :key="item.id"
                >
                    <div
                        v-if="hasChildren(item)"
                        class="nested-nav-group"
                    >
                        <button
                            type="button"
                            class="nested-nav-group-toggle"
                            :aria-expanded="isGroupExpanded(item.id)"
                            @click="toggleGroup(item.id)"
                        >
                            <span class="nested-nav-item-label">{{ item.name }}</span>
                            <font-awesome-icon
                                :icon="['fas', 'chevron-down']"
                                class="nested-nav-group-chevron"
                                :class="{ expanded: isGroupExpanded(item.id) }"
                            />
                        </button>
                        <transition name="nested-nav-collapse">
                            <div
                                v-if="isGroupExpanded(item.id)"
                                class="nested-nav-collapse-panel"
                            >
                                <div class="nested-nav-children">
                                    <button
                                        v-for="child in item.children"
                                        :key="child.id"
                                        type="button"
                                        class="nested-nav-item child"
                                        :class="{ selected: isItemSelected(child.id) }"
                                        :aria-current="isItemSelected(child.id) ? 'true' : undefined"
                                        @click="handleItemSelect(child.id)"
                                    >
                                        <span class="nested-nav-item-label">{{ child.name }}</span>
                                        <slot
                                            name="item-trailing"
                                            :item="child"
                                        />
                                    </button>
                                </div>
                            </div>
                        </transition>
                    </div>
                    <button
                        v-else
                        type="button"
                        class="nested-nav-item"
                        :class="{ selected: isItemSelected(item.id) }"
                        :aria-current="isItemSelected(item.id) ? 'true' : undefined"
                        @click="handleItemSelect(item.id)"
                    >
                        <span class="nested-nav-item-label">{{ item.name }}</span>
                        <slot
                            name="item-trailing"
                            :item="item"
                        />
                    </button>
                </template>
            </nav>
        </drawer>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import PrimevueButton from 'primevue/button';
import Drawer from 'primevue/drawer';
import { INestedNavItem } from '@/interfaces/general/INestedNavItem.ts';

const props = withDefaults(
    defineProps<{
        items: Array<INestedNavItem>;
        modelValue: string;
        ariaLabel?: string;
        drawerHeader?: string;
    }>(),
    {
        ariaLabel: 'Selection',
        drawerHeader: 'Navigate',
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();

const isDrawerVisible = ref<boolean>(false);
const expandedGroupIds = ref<Array<string>>([]);

const activeMatch = computed<{ parent?: INestedNavItem; leaf?: INestedNavItem }>(() => {
    const leafMatch = props.items.find(item => item.id === props.modelValue && !hasChildren(item));
    if (leafMatch) {
        return { leaf: leafMatch };
    }

    const parentWithLeaf = props.items.find(item => hasChildren(item)
        && item.children?.some(child => child.id === props.modelValue));
    if (!parentWithLeaf?.children) {
        return {};
    }

    const leaf = parentWithLeaf.children.find(child => child.id === props.modelValue);
    return {
        parent: parentWithLeaf,
        leaf,
    };
});

const activeLabel = computed<string>(() => {
    const { parent, leaf } = activeMatch.value;
    if (!leaf) return props.ariaLabel;
    if (parent) return `${parent.name} > ${leaf.name}`;
    return leaf.name;
});

const triggerAriaLabel = computed<string>(() => `Current selection: ${activeLabel.value}. Change selection`);

const navAriaLabel = computed<string>(() => `${props.ariaLabel} options`);

function hasChildren (item: INestedNavItem): boolean {
    return Array.isArray(item.children) && item.children.length > 0;
}

function isItemSelected (id: string): boolean {
    return id === props.modelValue;
}

function isGroupExpanded (groupId: string): boolean {
    return expandedGroupIds.value.includes(groupId);
}

function toggleGroup (groupId: string): void {
    if (isGroupExpanded(groupId)) {
        expandedGroupIds.value = expandedGroupIds.value.filter(id => id !== groupId);
        return;
    }
    expandedGroupIds.value = [...expandedGroupIds.value, groupId];
}

function ensureParentOfSelectionExpanded (): void {
    const parentId = activeMatch.value.parent?.id;
    if (!parentId || isGroupExpanded(parentId)) return;
    expandedGroupIds.value = [...expandedGroupIds.value, parentId];
}

function openDrawer (): void {
    ensureParentOfSelectionExpanded();
    isDrawerVisible.value = true;
}

function handleItemSelect (id: string): void {
    if (id !== props.modelValue) {
        emit('update:modelValue', id);
    }
    isDrawerVisible.value = false;
}

watch(() => props.modelValue, () => {
    ensureParentOfSelectionExpanded();
}, { immediate: true });
</script>

<style lang="scss" scoped>
.nested-nav-switcher {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: auto;
    max-width: none;
    min-width: 0;
}

.nested-nav-trigger {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    max-width: 100%;
    height: 100%;
    color: var(--primaryFont);
    font-size: var(--smallFontSize);
    font-weight: var(--boldFontWeight);
    border-bottom: 2px solid var(--accentColour);
    border-radius: 0;
}

.nested-nav-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.nested-nav-chevron {
    flex-shrink: 0;
    font-size: var(--extraSmallFontSize);
}

.nested-nav-list {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
}

.nested-nav-group-toggle,
.nested-nav-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--defaultSpacing);
    padding: var(--smallSpacing) var(--defaultSpacing);
    color: var(--primaryFont);
    font-size: var(--defaultFontSize);
    font-weight: var(--defaultFontWeight);
    text-decoration: none;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    width: 100%;

    &:hover {
        color: var(--accentColour);
    }
}

.nested-nav-group-toggle {
    font-weight: var(--boldFontWeight);
}

.nested-nav-group-chevron {
    flex-shrink: 0;
    font-size: var(--extraSmallFontSize);
    transition: transform 0.2s ease;

    &.expanded {
        transform: rotate(180deg);
    }
}

.nested-nav-collapse-panel {
    display: grid;
    grid-template-rows: 1fr;
}

.nested-nav-children {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
    overflow: hidden;
    min-height: 0;
}

.nested-nav-collapse-enter-active,
.nested-nav-collapse-leave-active {
    transition: grid-template-rows 0.2s ease, opacity 0.2s ease;
}

.nested-nav-collapse-enter-from,
.nested-nav-collapse-leave-to {
    grid-template-rows: 0fr;
    opacity: 0;
}

.nested-nav-collapse-enter-to,
.nested-nav-collapse-leave-from {
    grid-template-rows: 1fr;
    opacity: 1;
}

.nested-nav-item {
    &.child {
        padding-left: calc(var(--defaultSpacing) * 2);
    }

    &.selected {
        cursor: default;
        font-weight: var(--boldFontWeight);
        text-decoration: underline;
        text-decoration-color: var(--accentColour);
        text-underline-offset: var(--smallSpacing);
        text-decoration-thickness: 2px;

        &:hover {
            color: var(--primaryFont);
        }
    }
}

.nested-nav-item-label {
    white-space: nowrap;
}
</style>
